import {
  ComponentPropsWithoutRef,
  CSSProperties,
  ElementType,
  ReactElement,
  ReactNode,
} from 'react';
import './BgOverlay.scss';

type BgOverlayOwnProps = {
  children?: ReactNode;
  className?: string; // optional: allow extra classes to be merged
  color: 'black' | 'white' | 'theme-black' | 'theme-white';
  opacity: number;
};

type BgOverlayProps<AsType extends ElementType> = BgOverlayOwnProps & {
  as?: AsType;
} & Omit<ComponentPropsWithoutRef<AsType>, keyof BgOverlayOwnProps | 'as'>;

export const BgOverlay = <AsType extends ElementType = 'div'>(
  props: BgOverlayProps<AsType>,
): ReactElement | null => {
  const { as, children, className, color, opacity, style, ...rest } =
    props as BgOverlayProps<AsType> & { style?: CSSProperties };
  const AsType = as ?? 'div';

  const baseColor = (() => {
    switch (color) {
      case 'black':
        return '0, 0, 0';
      case 'white':
        return '255, 255, 255';
      case 'theme-black':
        // matches SCSS rgba(7, 28, 31, X)
        return '7, 28, 31';
      case 'theme-white':
        // themed white overlay – keep it simple as plain white channel values
        return '255, 255, 255';
      default:
        return '0, 0, 0';
    }
  })();

  const alpha = Math.min(1, Math.max(0, opacity / 100.0));
  const overlayBg = `rgba(${baseColor}, ${alpha})`;
  const overlayZ = color.startsWith('theme') ? 9 : 8;

  const mergedClassName = ['bg-overlay', className].filter(Boolean).join(' ');
  const mergedStyle: CSSProperties & Record<`--${string}`, string | number> = {
    ...style,
    '--bg-overlay-bg': overlayBg,
    '--bg-overlay-z': overlayZ,
  };

  return (
    <AsType className={mergedClassName} style={mergedStyle} {...rest}>
      {children}
    </AsType>
  );
};
